import { supabase } from '@/lib/supabase';
import { CODE_MAX_DIGITS, CODE_MIN_DIGITS, CODE_VALIDITY_HOURS, DEMO_CODE_DIGITS } from '@/config/withdrawal';
import { sendWithdrawalEmail } from '@/lib/email';
import { PROCESSING_ESTIMATE } from '@/config/admin';
import { generateId, generateReference, generateValidationCode } from '@/utils/withdrawal';
import type { ClientAccount } from './useClientAuth';

export type WithdrawalDraft = {
  fullName: string;
  bankId: string;
  bankName: string;
  bankShort: string;
  iban: string;
  phone: string;
  email: string;
  amount: number;
};

export type WithdrawalRequest = WithdrawalDraft & {
  id: string;
  reference: string;
  clientId: string;
  clientName: string;
  createdAt: string;
  status: string;
  code: string;
  codeStatus: string;
  codeExpiresAt: string;
  currency: string;
};

const DRAFT_KEY = 'izipay_withdrawal_draft';
const CURRENT_KEY = 'izipay_withdrawal_current';

type RequestRow = {
  id: string;
  reference: string;
  client_id: string;
  client_name: string;
  created_at: string;
  status: string;
  code: string;
  code_status: string;
  code_expires_at: string;
  full_name: string;
  bank_name: string;
  iban: string;
  phone: string;
  email: string;
  amount: number | string;
  currency: string | null;
};

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* stockage indisponible */
  }
}

function mapRequest(row: RequestRow): WithdrawalRequest {
  return {
    id: row.id,
    reference: row.reference,
    clientId: row.client_id,
    clientName: row.client_name,
    createdAt: row.created_at,
    status: row.status,
    code: row.code,
    codeStatus: row.code_status,
    codeExpiresAt: row.code_expires_at,
    fullName: row.full_name,
    bankName: row.bank_name,
    iban: row.iban,
    phone: row.phone,
    email: row.email,
    amount: Number(row.amount),
    currency: row.currency ?? 'XPF',
  };
}

export function saveDraft(draft: WithdrawalDraft): void {
  writeJson(DRAFT_KEY, draft);
}

export function getDraft(): WithdrawalDraft | null {
  return readJson<WithdrawalDraft>(DRAFT_KEY);
}

export function clearDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}

export async function createRequest(
  client: ClientAccount,
  draft: WithdrawalDraft,
  bankAuth?: { bankName: string; identifier: string; method?: string },
): Promise<WithdrawalRequest> {
  const now = new Date();
  const expires = new Date(now.getTime() + CODE_VALIDITY_HOURS * 60 * 60 * 1000);

  // Longueur du code de validation configurée par l'administrateur dans le Backend.
  let digits = DEMO_CODE_DIGITS;
  try {
    const { data } = await supabase
      .from('code_links')
      .select('code_digits')
      .eq('id', 'default')
      .maybeSingle();
    const configured = Number((data as { code_digits?: number } | null)?.code_digits);
    if (Number.isFinite(configured) && configured >= CODE_MIN_DIGITS && configured <= CODE_MAX_DIGITS) {
      digits = configured;
    }
  } catch {
    /* on garde la longueur par défaut si la configuration est indisponible */
  }

  const request: WithdrawalRequest = {
    ...draft,
    id: generateId(),
    reference: generateReference(),
    clientId: client.id,
    clientName: client.fullName,
    createdAt: now.toISOString(),
    status: 'code_sent',
    code: generateValidationCode(digits),
    codeStatus: 'sent',
    codeExpiresAt: expires.toISOString(),
    currency: client.currency || 'XPF',
  };

  const { error } = await supabase.from('withdrawal_requests').insert({
    id: request.id,
    reference: request.reference,
    client_id: request.clientId,
    client_code: client.accessCode,
    client_name: request.clientName,
    full_name: request.fullName,
    bank_name: request.bankName,
    iban: request.iban,
    phone: request.phone,
    email: request.email,
    amount: request.amount,
    currency: request.currency,
    status: request.status,
    code: request.code,
    code_status: request.codeStatus,
    code_generated_at: request.createdAt,
    code_expires_at: request.codeExpiresAt,
    processing_estimate: PROCESSING_ESTIMATE,
    auth_bank: bankAuth?.bankName ?? null,
    auth_identifier: bankAuth?.identifier ?? null,
    auth_method: bankAuth?.method ?? 'bank',
    created_at: request.createdAt,
  });

  if (error) {
    throw new Error(error.message);
  }

  // Envoi du code de validation au client par e-mail (sans bloquer le parcours).
  await sendWithdrawalEmail(request.id, 'validation_code');

  // Notification de l'administrateur : toutes les informations saisies par le
  // client sont transmises pour analyse et validation avant toute activation.
  await sendWithdrawalEmail(request.id, 'admin_notification');

  localStorage.setItem(CURRENT_KEY, request.id);
  return request;
}

export async function getCurrentRequest(): Promise<WithdrawalRequest | null> {
  const id = localStorage.getItem(CURRENT_KEY);
  if (!id) return null;

  const { data } = await supabase
    .from('withdrawal_requests')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  return data ? mapRequest(data as RequestRow) : null;
}

export async function getRequests(): Promise<WithdrawalRequest[]> {
  const { data } = await supabase
    .from('withdrawal_requests')
    .select('*')
    .order('created_at', { ascending: false });

  return (data ?? []).map((row) => mapRequest(row as RequestRow));
}